import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Botón de volver que respeta la navegación real, no una lista "padre" fija.
 *
 * Antes cada página de detalle volvía siempre al mismo listado (ej. el
 * detalle de OT siempre a `/ordenes`), sin importar desde dónde se llegó de
 * verdad — un flujo típico es Activo → Ubicación → OT relacionada, y desde
 * la OT el usuario esperaba volver a la ubicación, no saltar al listado de
 * OT. React Router marca `location.key` como `'default'` solo en la entrada
 * inicial de la sesión (llegada directa por URL, recarga, o pestaña nueva);
 * cualquier otra navegación dentro de la app le asigna una key real, así que
 * ese valor basta para distinguir "hay historial de esta sesión al que
 * volver" de "hay que caer a un destino fijo porque no hay a dónde volver".
 *
 * Esa heurística por sí sola falla si el navegador hace un reload completo
 * mientras el usuario está parado en la página de detalle (HMR en dev, F5,
 * abrir el link en una pestaña ya existente): React Router reinicia y esa
 * entrada pasa a ser la `'default'` de la nueva sesión, aunque el usuario
 * "vino" de otra pantalla — cayendo al fallback fijo en vez de a donde
 * realmente estaba. Por eso una página que sabe con certeza de dónde viene el
 * usuario (ej. un click sobre un evento del calendario) puede pasarlo
 * explícito por `state.volverA` al navegar; ese destino manda siempre que
 * exista, sin depender del historial del navegador.
 */
/**
 * La heurística de a dónde volver, sin el botón — para el puñado de pantallas
 * que necesitan este mismo comportamiento con su propio elemento visual (ej.
 * el link con texto de `EjecucionChecklist`, en vez del botón-flecha
 * genérico). Antes ese link usaba `<Link to=".../ordenes-trabajo/:id">`, que
 * EMPUJA una entrada de historial nueva en cada clic; alternado con el botón
 * atrás real del sistema, cada ida y vuelta agregaba una entrada más al
 * historial en vez de deshacer la anterior — el usuario terminaba rebotando
 * entre la OT y el checklist sin poder salir del todo (§ bug reportado).
 */
export function useVolver(a: string, estadoAlVolver?: Record<string, unknown>): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  const volverA = (location.state as { volverA?: string } | null)?.volverA;

  return function irAtras(): void {
    if (volverA) {
      // `replace`, no push: es una corrección del destino de "atrás", no un
      // paso hacia adelante — con push, un back del sistema inmediatamente
      // después reaparecía en la pantalla que se acababa de abandonar.
      navigate(volverA, { replace: true, state: estadoAlVolver });
      return;
    }
    if (location.key === 'default') navigate(a, { replace: true, state: estadoAlVolver });
    else navigate(-1);
  };
}

export function BotonVolver({
  a,
  etiqueta,
  interceptar,
}: {
  /** Destino de reserva cuando no hay historial de navegación (llegada directa, recarga). */
  a: string;
  /** Texto para lectores de pantalla, ej. "Volver a Órdenes de Trabajo". */
  etiqueta: string;
  /**
   * Si se define, en vez de navegar de una se le entrega la navegación real
   * para que decida cuándo dispararla — ej.: un formulario con cambios sin
   * guardar que primero confirma con un modal y solo si el usuario elige
   * descartar, dispara esta función.
   */
  interceptar?: (irAtras: () => void) => void;
}): JSX.Element {
  const irAtras = useVolver(a);

  return (
    <button
      type="button"
      className="boton-volver"
      aria-label={etiqueta}
      onClick={() => (interceptar ? interceptar(irAtras) : irAtras())}
    >
      ←
    </button>
  );
}
